import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, LogIn, UserPlus } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [systemSettings, setSystemSettings] = useState(null);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Initialize default admin and load system settings
    axios.post(`${API}/init`).catch(() => {});
    axios.get(`${API}/settings/system/public`).then(res => {
      setSystemSettings(res.data);
    }).catch(() => {});
    
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, senha);
      toast.success("Login realizado com sucesso!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao fazer login");
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = systemSettings?.cor_primaria || "#2563eb";

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-md animate-slide-up">
        <div className="text-center mb-8">
          {systemSettings?.logo_url ? (
            <img 
              src={systemSettings.logo_url} 
              alt="Logo" 
              className="h-16 mx-auto mb-4 object-contain"
            />
          ) : (
            <h1 
              className="text-4xl font-extrabold mb-2"
              style={{ color: primaryColor }}
            >
              8 Life
            </h1>
          )}
          <p className="text-muted-foreground">Sistema de Gestão de Igreja</p>
        </div>

        <Card className="border-border/50 shadow-xl">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-bold">Entrar</CardTitle>
            <CardDescription>
              Entre com suas credenciais para acessar o sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-mail ou telefone</Label>
                <Input
                  id="email"
                  type="text"
                  autoComplete="username"
                  placeholder="seu@email.com ou (00) 00000-0000"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email"
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="senha">Senha</Label>
                <div className="relative">
                  <Input
                    id="senha"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    required
                    data-testid="login-password"
                    className="h-12 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 text-base font-semibold gap-2"
                disabled={loading}
                data-testid="login-submit"
                style={{ backgroundColor: primaryColor }}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>
                    <LogIn className="h-5 w-5" />
                    Entrar
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-border/50">
              <p className="text-center text-sm text-muted-foreground mb-4">
                Novo por aqui?
              </p>
              <Button 
                variant="outline" 
                className="w-full h-12 gap-2"
                onClick={() => navigate("/cadastro-publico")}
                data-testid="public-register-link"
              >
                <UserPlus className="h-5 w-5" />
                Fazer meu cadastro
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
